import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import {
  ArrowRightIcon,
  ShoppingBagIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AgentOrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [priceChanges, setPriceChanges] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/field-agent/orders/${orderId}`);
      console.log('📦 Order details:', response.data);
      const orderData = response.data.order || response.data;
      setOrder(orderData);
      
      // Process items to ensure we have the correct price field
      const items = (orderData.items || []).map(item => ({
        ...item,
        price: item.unit_price || item.price || 0,
        original_price: item.unit_price || item.price || 0
      }));
      setEditedItems(items);
      setNotes(orderData.notes || '');
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('שגיאה בטעינת פרטי ההזמנה');
      if (error.response?.status === 404) {
        setTimeout(() => navigate('/agent/orders'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateItemQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setEditedItems(editedItems.map(item => 
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  const updateItemPrice = (itemId, newPrice) => {
    const item = editedItems.find(i => i.id === itemId);
    if (!item) return;
    
    const originalPrice = parseFloat(item.original_price);
    const updatedPrice = parseFloat(newPrice);
    
    // Track price changes
    if (originalPrice !== updatedPrice) {
      setPriceChanges(prev => ({
        ...prev,
        [itemId]: {
          original: originalPrice,
          new: updatedPrice,
          discount: originalPrice - updatedPrice,
          discountPercent: ((originalPrice - updatedPrice) / originalPrice * 100).toFixed(1)
        }
      }));
    } else {
      // Remove from tracking if price returned to original
      setPriceChanges(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    }
    
    setEditedItems(editedItems.map(item => 
      item.id === itemId ? { ...item, price: updatedPrice } : item
    ));
  };

  const removeItem = (itemId) => {
    if (editedItems.length === 1) {
      toast.error('חייבת להישאר לפחות פריט אחד בהזמנה');
      return;
    }
    setEditedItems(editedItems.filter(item => item.id !== itemId));
  };

  const handleSave = async () => {
    // Validate change reason if there are price changes
    if (Object.keys(priceChanges).length > 0 && !changeReason.trim()) {
      toast.error('יש להזין סיבה לשינוי המחירים');
      return;
    }

    try {
      setSaving(true);
      
      // Prepare price changes documentation
      const priceChangesDocs = Object.entries(priceChanges).map(([itemId, change]) => {
        const item = editedItems.find(i => i.id === itemId);
        return {
          product_id: item.product_id || item.product?.id,
          product_name: item.product?.name || item.name,
          original_price: change.original,
          new_price: change.new,
          discount: change.discount,
          discount_percent: change.discountPercent
        };
      });

      await axios.put(`/api/field-agent/orders/${orderId}`, {
        items: editedItems.map(item => ({
          product_id: item.product_id || item.product?.id,
          quantity: item.quantity,
          price: item.price
        })),
        notes: notes,
        change_reason: changeReason.trim() || undefined,
        price_changes: priceChangesDocs.length > 0 ? priceChangesDocs : undefined
      });
      
      toast.success('ההזמנה עודכנה בהצלחה');
      setEditMode(false);
      setChangeReason('');
      setPriceChanges({});
      fetchOrderDetails();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('שגיאה בעדכון ההזמנה: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedItems((order?.items || []).map(item => ({
      ...item,
      price: item.unit_price || item.price || 0,
      original_price: item.unit_price || item.price || 0
    })));
    setNotes(order?.notes || '');
    setChangeReason('');
    setPriceChanges({});
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'תאריך לא זמין';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'תאריך לא תקין';
      }
      
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'שגיאה בתאריך';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      pending: <ClockIcon className="h-6 w-6 text-yellow-500" />,
      processing: <TruckIcon className="h-6 w-6 text-blue-500" />,
      shipped: <TruckIcon className="h-6 w-6 text-purple-500" />,
      delivered: <CheckCircleIcon className="h-6 w-6 text-green-500" />,
      cancelled: <XCircleIcon className="h-6 w-6 text-red-500" />
    };
    return iconMap[status] || <ShoppingBagIcon className="h-6 w-6 text-gray-500" />;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'ממתין לטיפול', className: 'bg-yellow-100 text-yellow-800' },
      processing: { text: 'בטיפול', className: 'bg-blue-100 text-blue-800' },
      shipped: { text: 'נשלח', className: 'bg-purple-100 text-purple-800' },
      delivered: { text: 'נמסר', className: 'bg-green-100 text-green-800' },
      cancelled: { text: 'בוטל', className: 'bg-red-100 text-red-800' }
    };

    const badge = statusMap[status] || { text: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => {
      // Tester and bonus items are always 0 price
      if (item.is_tester || item.is_bonus) {
        return sum;
      }
      const price = parseFloat(item.unit_price || item.price || item.total_price / item.quantity || 0);
      return sum + (item.quantity * price);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">הזמנה לא נמצאה</h3>
        <p className="mt-1 text-sm text-gray-500">מספר הזמנה: {orderId}</p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/agent/orders')}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            חזרה להזמנות
          </button>
        </div>
      </div>
    );
  }

  const items = editMode ? editedItems : (order.items || []);
  const total = calculateTotal(items);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/agent/orders')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowRightIcon className="h-4 w-4 ml-1" />
            חזרה להזמנות
          </button>
          <h1 className="text-3xl font-bold text-gray-900">הזמנה #{order.order_number || order.id?.slice(0, 8)}</h1>
          <p className="mt-1 text-sm text-gray-500">
            נוצרה ב-{formatDate(order.created_at || order.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {getStatusIcon(order.status)}
          {getStatusBadge(order.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">פריטי ההזמנה</h2>
              {!editMode && order.status === 'pending' && (
                <button
                  onClick={() => setEditMode(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                >
                  <PencilIcon className="h-4 w-4 ml-1" />
                  ערוך הזמנה
                </button>
              )}
              {editMode && (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'שומר...' : 'שמור'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    ביטול
                  </button>
                </div>
              )}
            </div>
            <div className="divide-y divide-gray-200">
              {items.map((item, index) => (
                <div key={item.id || index} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900">
                        {item.product?.name || item.name || 'מוצר'}
                      </h3>
                      {(item.product?.sku || item.sku) && (
                        <p className="mt-1 text-sm text-gray-500">מק"ט: {item.product?.sku || item.sku}</p>
                      )}
                      <div className="mt-2 space-y-2">
                        {editMode ? (
                          <>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600 font-medium">מחיר יחידה:</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.price}
                                  onChange={(e) => updateItemPrice(item.id, e.target.value)}
                                  className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                                <span className="text-xs text-gray-500">₪</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600 font-medium">כמות:</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value))}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                            </div>
                            {priceChanges[item.id] && (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2">
                                <p className="text-xs text-yellow-800">
                                  <span className="font-semibold">שינוי מחיר:</span> מ-{formatCurrency(priceChanges[item.id].original)} ל-{formatCurrency(priceChanges[item.id].new)}
                                  {priceChanges[item.id].discount > 0 ? (
                                    <span className="text-green-700 font-medium"> (הנחה של {priceChanges[item.id].discountPercent}%)</span>
                                  ) : (
                                    <span className="text-red-700 font-medium"> (תוספת מחיר)</span>
                                  )}
                                </p>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">
                              מחיר יחידה: {formatCurrency(item.unit_price || item.price || 0)}
                            </span>
                            <span className="text-sm text-gray-600">
                              כמות: {item.quantity}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="text-lg font-semibold text-gray-900">
                        {formatCurrency(item.quantity * (item.unit_price || item.price || 0))}
                      </p>
                      {editMode && (
                        <button
                          onClick={() => removeItem(item.id)}
                          className="mt-2 text-sm text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-4 w-4 inline ml-1" />
                          הסר
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 px-6 py-4 space-y-2">
              {/* Subtotal - always use calculated total to ensure bonus/tester items show as 0 */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">סכום ביניים:</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(total)}
                </span>
              </div>
              
              {/* Discount */}
              {order.discount_amount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-yellow-600">
                    הנחה {order.metadata?.discount_info && (
                      <span className="text-xs">
                        ({order.metadata.discount_info.type === 'percentage' 
                          ? `${order.metadata.discount_info.value}%` 
                          : 'סכום קבוע'})
                      </span>
                    )}:
                  </span>
                  <span className="font-medium text-yellow-600">
                    -{formatCurrency(order.discount_amount)}
                  </span>
                </div>
              )}
              
              {/* Tax */}
              {order.tax_amount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">מע"מ (18%):</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(order.tax_amount)}
                  </span>
                </div>
              )}
              
              {/* Total */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <span className="text-base font-medium text-gray-900">סה"כ לתשלום:</span>
                <span className="text-2xl font-bold text-primary-600">
                  {formatCurrency(order.total_amount || total)}
                </span>
              </div>
            </div>
          </div>

          {/* Discount Information */}
          {order.metadata?.discount_info && (
            <div className="bg-yellow-50 border border-yellow-200 shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-yellow-900 mb-3 flex items-center">
                <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold ml-2">%</span>
                פרטי הנחה
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-yellow-800 font-medium">סכום הנחה:</dt>
                  <dd className="text-yellow-900 font-bold">
                    ₪{order.metadata.discount_info.amount.toFixed(2)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-800 font-medium">סוג הנחה:</dt>
                  <dd className="text-yellow-900">
                    {order.metadata.discount_info.type === 'percentage' 
                      ? `${order.metadata.discount_info.value}%` 
                      : 'סכום קבוע'}
                  </dd>
                </div>
                {order.metadata.discount_info.reason && (
                  <div className="pt-2 border-t border-yellow-200">
                    <dt className="text-yellow-800 font-medium mb-1">סיבת הנחה:</dt>
                    <dd className="text-yellow-900 bg-yellow-100 p-2 rounded">
                      {order.metadata.discount_info.reason}
                    </dd>
                  </div>
                )}
                {order.metadata.discount_info.appliedBy && (
                  <div className="pt-2 border-t border-yellow-200 text-xs text-yellow-700">
                    <div>נותן ההנחה: {order.metadata.discount_info.appliedBy.agent_name}</div>
                    <div>תאריך: {new Date(order.metadata.discount_info.appliedBy.timestamp).toLocaleString('he-IL')}</div>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Item-Level Discounts */}
          {order.metadata?.item_discounts && order.metadata.item_discounts.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-orange-900 mb-3">
                הנחות למוצרים ספציפיים
              </h3>
              <div className="space-y-3">
                {order.metadata.item_discounts.map((itemDisc, idx) => (
                  <div key={idx} className="bg-white p-3 rounded border border-orange-200">
                    <div className="font-medium text-gray-900 mb-1">
                      {itemDisc.product_name}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex justify-between">
                        <span>מחיר מקורי:</span>
                        <span className="line-through">₪{itemDisc.originalPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>מחיר אחרי הנחה:</span>
                        <span className="font-bold text-orange-600">₪{itemDisc.discountedPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>אחוז הנחה:</span>
                        <span className="font-bold text-orange-600">{itemDisc.discountPercent}%</span>
                      </div>
                    </div>
                    {itemDisc.appliedBy && (
                      <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                        על ידי: {itemDisc.appliedBy.agent_name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">הערות להזמנה</h3>
            {editMode ? (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="הוסף הערות להזמנה..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {order.notes || 'אין הערות'}
              </p>
            )}
          </div>

          {/* Price Change Reason (only in edit mode if there are price changes) */}
          {editMode && Object.keys(priceChanges).length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-amber-900 mb-3 flex items-center">
                <span className="bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold ml-2">!</span>
                סיבת שינוי המחירים (חובה)
              </h3>
              <p className="text-sm text-amber-800 mb-3">
                זוהו שינויי מחיר. אנא הסבר את הסיבה לשינוי כדי שהמנהל יבין את ההחלטה.
              </p>
              <textarea
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="לדוגמה: הנחה מיוחדת ללקוח VIP, תיקון טעות במחיר, פיצוי על עיכוב בשילוח..."
                rows={3}
                className="w-full px-3 py-2 border-2 border-amber-400 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                required
              />
              <div className="mt-3 bg-white rounded-md p-3 border border-amber-200">
                <p className="text-xs font-semibold text-amber-900 mb-2">סיכום שינויים:</p>
                {Object.entries(priceChanges).map(([itemId, change]) => {
                  const item = editedItems.find(i => i.id === itemId);
                  return (
                    <div key={itemId} className="text-xs text-amber-800 mb-1">
                      • <strong>{item?.product?.name || item?.name}</strong>: {formatCurrency(change.original)} → {formatCurrency(change.new)} 
                      {change.discount > 0 ? (
                        <span className="text-green-700 font-medium"> (הנחה {change.discountPercent}%)</span>
                      ) : (
                        <span className="text-red-700 font-medium"> (עלייה במחיר)</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 ml-2 text-gray-400" />
              פרטי לקוח
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">שם החברה</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.company?.name || order.customer?.company_name || 'לא זמין'}
                </dd>
              </div>
              {order.company?.contact_person && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">איש קשר</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.company.contact_person}</dd>
                </div>
              )}
              {order.company?.phone && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">טלפון</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.company.phone}</dd>
                </div>
              )}
              {order.company?.email && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">אימייל</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.company.email}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Shipping Address */}
          {(order.shipping_address || order.shippingAddress) && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <TruckIcon className="h-5 w-5 ml-2 text-gray-400" />
                כתובת משלוח
              </h3>
              {(() => {
                const addr = order.shipping_address || order.shippingAddress || {};
                return (
                  <dl className="space-y-3">
                    {(addr.full_name || addr.first_name) && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">שם</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {addr.full_name || `${addr.first_name || ''} ${addr.last_name || ''}`.trim()}
                        </dd>
                      </div>
                    )}
                    {(addr.street || addr.address_line_1 || addr.address_line1) && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">כתובת</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {addr.street || addr.address_line_1 || addr.address_line1}
                          {addr.address_line_2 || addr.address_line2 ? `, ${addr.address_line_2 || addr.address_line2}` : ''}
                        </dd>
                      </div>
                    )}
                    {addr.city && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">עיר</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {addr.city}{addr.postal_code ? ` - ${addr.postal_code}` : ''}
                        </dd>
                      </div>
                    )}
                    {addr.phone && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">טלפון</dt>
                        <dd className="mt-1 text-sm text-gray-900">{addr.phone}</dd>
                      </div>
                    )}
                  </dl>
                );
              })()}
            </div>
          )}

          {/* Order Timeline */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 ml-2 text-gray-400" />
              ציר זמן
            </h3>
            <div className="flow-root">
              <ul className="-mb-8">
                <li>
                  <div className="relative pb-8">
                    <div className="relative flex space-x-3 space-x-reverse">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                          <CheckCircleIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 font-medium">הזמנה נוצרה</p>
                        <p className="text-xs text-gray-500">{formatDate(order.created_at || order.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </li>
                {order.updated_at && order.updated_at !== order.created_at && (
                  <li>
                    <div className="relative">
                      <div className="relative flex space-x-3 space-x-reverse">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                            <ClockIcon className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium">עדכון אחרון</p>
                          <p className="text-xs text-gray-500">{formatDate(order.updated_at || order.updatedAt)}</p>
                        </div>
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Audit Trail / Change History */}
          {order.metadata?.audit_trail && order.metadata.audit_trail.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 ml-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                היסטוריית שינויים
              </h3>
              <div className="space-y-4">
                {order.metadata.audit_trail.map((audit, idx) => (
                  <div key={idx} className="border-r-4 border-amber-400 bg-amber-50 p-4 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {audit.change_log?.agent_name || 'סוכן'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(audit.change_log?.timestamp)}
                      </p>
                    </div>

                    {audit.change_reason && (
                      <div className="mb-3 p-2 bg-white rounded border border-amber-200">
                        <p className="text-xs font-semibold text-amber-900 mb-1">סיבת השינוי:</p>
                        <p className="text-sm text-gray-700">{audit.change_reason}</p>
                      </div>
                    )}

                    {audit.price_changes && audit.price_changes.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-700">שינויי מחיר:</p>
                        {audit.price_changes.map((change, changeIdx) => (
                          <div key={changeIdx} className="text-xs bg-white p-2 rounded border border-gray-200">
                            <p className="font-medium text-gray-900">{change.product_name}</p>
                            <p className="text-gray-600">
                              {formatCurrency(change.original_price)} → {formatCurrency(change.new_price)}
                              {change.discount > 0 ? (
                                <span className="text-green-600 font-semibold"> (הנחה {change.discount_percent}%)</span>
                              ) : (
                                <span className="text-red-600 font-semibold"> (עלייה במחיר)</span>
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {audit.previous_total !== audit.new_total && (
                      <div className="mt-3 pt-3 border-t border-amber-200">
                        <p className="text-xs text-gray-600">
                          סה"כ הזמנה: {formatCurrency(audit.previous_total)} → {formatCurrency(audit.new_total)}
                          <span className={`font-semibold ${audit.new_total < audit.previous_total ? 'text-green-600' : 'text-red-600'}`}>
                            {' '}({audit.new_total < audit.previous_total ? '-' : '+'}{formatCurrency(Math.abs(audit.new_total - audit.previous_total))})
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentOrderDetailsPage;

